import { css } from '@emotion/react';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { CircularProgress, Typography } from '@mui/material';
import Title from 'components/Title/Title';
import Page from 'components/Page/Page';

import fetchNodes from 'store/fetchNodes';
import useNodesAsync from 'store/useNodesAsync';
import { IWorkerSummary } from 'types/INodeAgent';

import NodeTable from './NodeTable';
import StartWorkerDialog from './StartWorkerDialog';
import { slotBudgetOf } from 'utils/slotBudget';

export default function NodesPage() {
  const nodesAsync = useNodesAsync();
  const [startTarget, setStartTarget] = useState<{ nodeId: string; template?: IWorkerSummary } | null>(null);
  const [provisioningNodeId, setProvisioningNodeId] = useState<string | null>(null);
  const baselineWorkerCount = useRef<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const nudgeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startPolling = useCallback((intervalMs: number) => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(fetchNodes, intervalMs);
  }, []);

  const nudge = useCallback(() => {
    fetchNodes();
    startPolling(3000);
    if (nudgeTimerRef.current) clearTimeout(nudgeTimerRef.current);
    nudgeTimerRef.current = setTimeout(() => startPolling(15000), 30000);
  }, [startPolling]);

  useEffect(() => {
    fetchNodes();
    startPolling(15000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (nudgeTimerRef.current) clearTimeout(nudgeTimerRef.current);
    };
  }, [startPolling]);

  // 30s timeout to clear provisioning banner
  useEffect(() => {
    if (!provisioningNodeId) return;
    const timer = setTimeout(() => setProvisioningNodeId(null), 30000);
    return () => clearTimeout(timer);
  }, [provisioningNodeId]);

  // Clear provisioning banner when worker count increases
  useEffect(() => {
    if (
      !provisioningNodeId ||
      baselineWorkerCount.current === null ||
      (nodesAsync.status !== 'SUCCESS' && nodesAsync.status !== 'REFRESH')
    )
      return;
    const node = nodesAsync.data.find((n) => n.id === provisioningNodeId);
    if (node && (node.worker_summaries?.length ?? 0) > baselineWorkerCount.current) {
      setProvisioningNodeId(null);
    }
  }, [nodesAsync, provisioningNodeId]);

  // A new worker is on its way on this node: show the provisioning banner until
  // a row appears. Shared by the Start dialog and the rolling path of Upgrade.
  const beginProvisioning = useCallback(
    (nodeId: string) => {
      if (nodesAsync.status !== 'SUCCESS' && nodesAsync.status !== 'REFRESH') return;
      const node = nodesAsync.data.find((n) => n.id === nodeId);
      baselineWorkerCount.current = node?.worker_summaries?.length ?? 0;
      setProvisioningNodeId(nodeId);
      nudge();
    },
    [nodesAsync, nudge],
  );

  const handleWorkerSubmit = useCallback(() => {
    if (!startTarget) return;
    beginProvisioning(startTarget.nodeId);
    setStartTarget(null);
  }, [startTarget, beginProvisioning]);

  const loadedNodes = nodesAsync.status === 'SUCCESS' || nodesAsync.status === 'REFRESH' ? nodesAsync.data : [];
  const startNode = startTarget ? loadedNodes.find((n) => n.id === startTarget.nodeId) : undefined;
  const startNodeWorkers = startNode?.worker_summaries ?? [];

  return (
    <Fragment>
      <Title>Nodes | Tailor</Title>
      <Page>
        <div
          css={css`
            margin: 48px;

            @media (max-width: 767px) {
              margin: 12px;
            }
          `}
        >
          <Typography variant="h5" sx={{ mb: 1 }}>
            Nodes
          </Typography>

          {(nodesAsync.status === 'INIT' || nodesAsync.status === 'PENDING') && <CircularProgress />}

          {nodesAsync.status === 'ERROR' && (
            <Typography color="error">Failed to load node agents. Please try again.</Typography>
          )}

          {(nodesAsync.status === 'SUCCESS' || nodesAsync.status === 'REFRESH') && (
            <NodeTable
              nodes={nodesAsync.data}
              onStartWorker={(nodeId, template) => setStartTarget({ nodeId, template })}
              provisioningNodeId={provisioningNodeId}
              onProvisioning={beginProvisioning}
              onMutate={nudge}
            />
          )}
        </div>

        {startTarget !== null && (
          <StartWorkerDialog
            nodeAgentId={startTarget.nodeId}
            agentPytailorVersion={startNode?.pytailor_version ?? null}
            workers={startNodeWorkers}
            template={startTarget.template}
            slotBudget={slotBudgetOf(startNode)}
            workdirPackages={Object.fromEntries(
              startNodeWorkers
                .filter((ws) => ws.workdir_name && ws.package_name)
                .map((ws) => [ws.workdir_name as string, ws.package_name as string]),
            )}
            workdirSoftware={Object.fromEntries(
              startNodeWorkers
                .filter((ws) => ws.workdir_name && ws.software)
                .map((ws) => [ws.workdir_name as string, ws.software as string]),
            )}
            onClose={() => setStartTarget(null)}
            onSubmit={handleWorkerSubmit}
          />
        )}
      </Page>
    </Fragment>
  );
}
